import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Layer } from 'grommet';
import { ClientFileTypeService } from '/src/api/services/ClientFileTypeService';
import { Box, Card, CardBody, CardHeader, Input, Select, Text } from '/src/components';
import FileInput from '/src/components/common/FileInput';
import { ClientFileType } from '/src/lib/models/ClientFileType';

type UploadDocumentModalProps = {
  onClose: () => void;
  onUpload: (file: File, typeId: number, comments: string | null) => void;
};

const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'gif', 'png', 'pdf', 'csv', 'doc', 'docx', 'xls', 'xlsx'];

export const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({ onClose, onUpload }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [comments, setComments] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [documentTypes, setDocumentTypes] = useState<ClientFileType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchDocumentTypes = async () => {
      try {
        const types = await ClientFileTypeService.listClientFileTypes();
        setDocumentTypes(types);
      } catch (error) {
        console.error('Error fetching document types:', error);
        setError('Failed to load document types');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocumentTypes();
  }, []);

  const validateFile = (file: File): boolean => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
      setError(`Invalid file type. Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return false;
    }
    setError(null);
    return true;
  };

  const handleFileChange = (file: File) => {
    if (validateFile(file)) {
      setSelectedFile(file);
    } else {
      setSelectedFile(null);
    }
  };

  const handleUploadClick = () => {
    if (!selectedFile || !selectedTypeId) return;

    try {
      const typeId = typeof selectedTypeId === 'object' ? (selectedTypeId as { id: number }).id : selectedTypeId;
      const processedComments = comments === null ? null : comments.trim() || null;
      onUpload(selectedFile, typeId, processedComments);
    } catch (error) {
      console.error('Error in handleUploadClick:', error);
      setError('Failed to start upload process');
    }
  };

  return (
    <Layer
      position="center"
      onEsc={onClose}
      responsive={false}
      style={{
        width: 'auto',
        maxWidth: 'min(95vw, 500px)',
        maxHeight: '95vh',
        overflow: 'auto',
        borderRadius: '8px',
      }}
    >
      <Card width="100%" style={{ width: '100%' }}>
        <CardHeader title={t('documents:uploadDocument')} />
        <CardBody
          pad={{
            vertical: 'medium',
            horizontal: 'large',
            ...(typeof window !== 'undefined' && window.innerWidth < 768
              ? {
                  horizontal: 'medium',
                }
              : {}),
          }}
        >
          <Box gap="medium" style={{ maxWidth: '100%', overflow: 'hidden' }}>
            <Box>
              <FileInput
                onFileSelect={handleFileChange}
                accept={ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',')}
                selectedFile={selectedFile}
              />
              {error && (
                <Box direction="row" gap="small" margin={{ top: '0.5rem', left: '0.75rem' }}>
                  <Text color="status-critical" size="small">
                    {error}
                  </Text>
                </Box>
              )}
            </Box>

            <Select
              label={t('documents:documentType')}
              options={documentTypes.map((type) => ({ label: type.name, value: type.id }))}
              value={selectedTypeId || ''}
              setValue={(value: number | string) => setSelectedTypeId(Number(value))}
              placeholder={t('documents:selectDocumentType')}
              disabled={isLoading}
              required
            />

            <Box flex="grow" height={{ min: '6rem' }}>
              <Input
                label={t('common:comments')}
                placeholder={t('documents:addComments')}
                value={comments ?? ''}
                setValue={(value: string) => {
                  setComments(value === '' ? null : value);
                }}
                maxLength={500}
                componentType="textArea"
              />
            </Box>
            <Text size="small" color="dark-6" margin={{ top: '-1rem' }}>
              {(comments?.length || 0) + t('documents:characterLimit')}
            </Text>
          </Box>

          <Box direction="row" gap="small" justify="end">
            <Button label={t('documents:cancel')} onClick={onClose} style={{ fontWeight: 'normal' }} />
            <Button
              label={t('documents:upload')}
              primary
              onClick={handleUploadClick}
              disabled={!selectedFile || !selectedTypeId}
              style={{ fontWeight: 'normal' }}
            />
          </Box>
        </CardBody>
      </Card>
    </Layer>
  );
};
